import React from 'react';
import { useSelector } from 'react-redux';
import { leadNamesSelector } from '../../ecg/selectors/signal.selectors';
import { Ruler } from '../../ruler/components/Ruler';
import { Plot } from '../contexts/plot.context';
import { Row } from '../contexts/row.context';
import { Strip } from '../contexts/strip.context';
import { squareToPixel, THREE_LEAD } from '../utils/graph.utils';
import { AxisContainer } from './AxisContainer';
import { EditableStrip } from './EditableStrip';
import { GainPickerContainer } from './GainPickerContainer';
import { GridContainer } from './GridContainer';
import { LeadPickerContainer } from './LeadPickerContainer';
import { ManualQrsBar } from './ManualQrsBar';
import { NoiseBar } from './NoiseBar';
import { PlotHeader } from './PlotHeader';
import { QRSCount } from './QrsCount';
import { QrsWaves } from './QrsWaves';
import { BeatIntervals } from './BeatIntervals';
import { STRIP_EDITOR_WIDTH, StripEditor } from './StripEditor';
import { StripPosition } from './StripPosition';
import { WaveTags } from './WaveTags';
import { RulerContainer } from '../../ruler/components/RulerContainer';
import { isToolActiveSelector } from '../../tool/selectors/tool.selectors';
import { DefaultLeads } from './DefaultLeads';
import {
  RULER_HEADER_HEIGHT,
  RulerInfo,
} from '../../ruler/components/RulerInfo';
import { Tool } from '../../tool/utils/tool.utils';
import { ShrIntervals } from './ShrIntervals';
import { showShrSelector } from '../../auth/selectors/auth.selectors';

const STRIP_HEIGHT = squareToPixel(4);
const STRIP_COUNT = 3;
const TOTAL_HEIGHT = STRIP_COUNT * STRIP_HEIGHT;
export const THREE_LEAD_DURATION = 10_000; // 10s

export const getThreeLeadStripId = (key: number | string) =>
  `${THREE_LEAD}-strip-${key}`;

export const ThreeLeadPlot: React.FC = () => {
  const leads = useSelector(leadNamesSelector);
  const displayedLeads = leads.slice(0, STRIP_COUNT);
  const availableHeight = TOTAL_HEIGHT / displayedLeads.length;
  const padding = (availableHeight - STRIP_HEIGHT) / 2;
  const isEpisodeMeasurementActive = useSelector(
    isToolActiveSelector(Tool.episodeMeasurement),
  );
  const showShr = useSelector(showShrSelector);

  return (
    <Strip id={getThreeLeadStripId('root')} duration={THREE_LEAD_DURATION}>
      <Plot>
        <PlotHeader offset={STRIP_EDITOR_WIDTH} height={RULER_HEADER_HEIGHT}>
          <RulerInfo />
        </PlotHeader>
        {showShr && (
          <PlotHeader offset={STRIP_EDITOR_WIDTH}>
            <ShrIntervals />
          </PlotHeader>
        )}
        <PlotHeader offset={STRIP_EDITOR_WIDTH} dataTestId='beat-intervals'>
          <WaveTags />
          <BeatIntervals />
        </PlotHeader>
      </Plot>
      <Plot
        plotName='lead'
        pointerEvents={isEpisodeMeasurementActive ? 'none' : undefined}
      >
        {displayedLeads.map((lead, index) => (
          <Strip
            key={index}
            id={getThreeLeadStripId(index)}
            duration={THREE_LEAD_DURATION}
            leads={[lead]}
          >
            <Row height={STRIP_HEIGHT} padding={padding}>
              <StripEditor>
                <GainPickerContainer leadsShowing={true} />
                <LeadPickerContainer />
              </StripEditor>
              <EditableStrip>
                <GridContainer />
                <StripPosition>
                  <DefaultLeads />
                  <QrsWaves />
                  <RulerContainer>
                    <Ruler />
                  </RulerContainer>
                  {index === displayedLeads.length - 1 && (
                    <>
                      <ManualQrsBar />
                      <QRSCount />
                    </>
                  )}
                </StripPosition>
              </EditableStrip>
            </Row>
          </Strip>
        ))}
      </Plot>
      <Plot>
        <NoiseBar offset={STRIP_EDITOR_WIDTH} />
        <AxisContainer offset={STRIP_EDITOR_WIDTH} />
      </Plot>
    </Strip>
  );
};
